import React from 'react';
import { User, UserProgress, Chapter, Level, Achievement } from '../types';

interface HomePageProps {
  user: User | null;
  progress: UserProgress | null;
  chapters: Chapter[];
  levels: Level[];
  achievements: Achievement[];
  onNavigate: (page: 'home' | 'challenge' | 'quiz' | 'profile' | 'result') => void;
}

const HomePage: React.FC<HomePageProps> = ({
  user,
  progress,
  chapters,
  levels,
  achievements,
  onNavigate
}) => {
  const completedCount = progress?.completedLevels.length || 0;
  const totalLevels = levels.length;
  const progressPercentage = Math.round((completedCount / totalLevels) * 100);

  const unlockedAchievements = achievements.filter(a => 
    progress?.achievements.includes(a.id)
  );

  return (
    <div className="fade-in">
      <div className="header">
        <h1>🧪 高二化学闯关测试</h1>
        <div className="subtitle">北京海淀区 · 2024-2025期中真题汇编</div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>题目来源：清华附中、人大附中、一零一中等名校期中试卷</div>
      </div>

      <div className="main-content">
        {/* 用户信息卡片 */}
        <div className="chapter-card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ 
              fontSize: '48px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '12px'
            }}>
              {user?.avatar || '👨‍🎓'}
            </div>
            <div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {user?.nickname || '化学学习者'}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                {user?.school} · {user?.grade}{user?.class}
              </div>
            </div>
          </div>

          <div className="stats-bar" style={{ marginBottom: '0' }}>
            <div className="stat-item">
              <div className="stat-value">{progress?.totalScore || 0}</div>
              <div className="stat-label">总积分</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{progress?.dailyChallenges || 3}</div>
              <div className="stat-label">今日挑战</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{progress?.streak || 0}</div>
              <div className="stat-label">连续天数</div>
            </div>
          </div>
        </div>

        {/* 学习进度 */}
        <div className="chapter-card">
          <div className="chapter-header">
            <div className="chapter-title">📊 学习进度</div>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '8px',
              fontSize: '14px'
            }}>
              <span style={{ color: '#6b7280' }}>关卡完成</span>
              <span style={{ fontWeight: '600', color: '#3b82f6' }}>
                {completedCount}/{totalLevels}
              </span>
            </div>
            <div style={{ 
              background: '#e5e7eb', 
              borderRadius: '8px', 
              height: '12px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${progressPercentage}%`, 
                height: '100%',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {levels.map(level => (
              <div
                key={level.id}
                style={{
                  padding: '8px 12px',
                  background: level.unlocked ? '#e0e7ff' : '#f3f4f6',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: level.unlocked ? '#3b82f6' : '#9ca3af',
                  cursor: level.unlocked ? 'pointer' : 'not-allowed'
                }}
                onClick={() => level.unlocked && onNavigate('challenge')}
              >
                {level.id}
              </div>
            ))}
          </div>
        </div>

        {/* 章节概览 */}
        {chapters.map(chapter => (
          <div key={chapter.id} className="chapter-card">
            <div className="chapter-header">
              <div>
                <div className="chapter-badge" style={{ 
                  background: chapter.id === 1 ? '#3b82f6' : '#8b5cf6',
                  marginBottom: '4px'
                }}>
                  第{chapter.id}章
                </div>
                <div className="chapter-title">{chapter.title}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                  {chapter.totalQuestions}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>题目数</div>
              </div>
            </div>
            
            {chapter.sections.map(section => (
              <div key={section.id} style={{ 
                fontSize: '14px', 
                color: '#6b7280',
                padding: '8px 0',
                borderBottom: '1px solid #f3f4f6'
              }}>
                {section.title} · {section.questionCount}题
              </div>
            ))}
          </div>
        ))}

        {/* 成就展示 */}
        <div className="chapter-card">
          <div className="chapter-header">
            <div className="chapter-title">🏆 我的成就</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {unlockedAchievements.length}/{achievements.length}
            </div>
          </div>
          
          {achievements.slice(0, 4).map(achievement => {
            const isUnlocked = progress?.achievements.includes(achievement.id);
            return (
              <div 
                key={achievement.id} 
                className="achievement-item"
                style={{ 
                  opacity: isUnlocked ? 1 : 0.5,
                  background: isUnlocked ? '#f0fdf4' : '#f9fafb'
                }}
              >
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-info">
                  <div className="achievement-title">{achievement.title}</div>
                  <div className="achievement-desc">{achievement.description}</div>
                </div>
                {isUnlocked && (
                  <div className="achievement-unlocked">已获得</div>
                )}
              </div>
            );
          })}
        </div>

        {/* 开始闯关按钮 */}
        <button 
          className="btn btn-primary pulse"
          style={{ 
            width: '100%', 
            marginTop: '20px',
            fontSize: '18px',
            padding: '16px'
          }}
          onClick={() => onNavigate('challenge')}
        >
          🎯 开始闯关
        </button>
      </div>
    </div>
  );
};

export default HomePage;
